import styled from 'styled-components'
import { HEADER_DEFAULT_Y, CATEGORY_HEADER_TYPE } from '@/features/shared/constants/global'
import {
  MOBILE_BG_POSITIONER_ID,
  MOBILE_STICKY_NAV_ID,
} from '@/features/shared/hooks/useHeaderRect'
import { GridLayoutContainer } from '@/features/shared/styles/layout'
import { HeaderSearch } from '@/features/shared/components/Headers/Header/HeaderSearch'
import { LogoAndStoreNav } from '@/features/shared/components/Headers/Header/LogoAndStoreNav'
import { RightMenuItems } from '@/features/shared/components/Headers/Header/RightMenuItems'
import { HamburgerMenuButton } from '@/features/shared/components/HamburgerMenu/HamburgerMenuButton'
import { useScrollWithThreshold } from '@/features/shared/components/Headers/useScrollWithThreshold'
import { screenSizes } from '@shipt/design-system-themes'
import { spacingPx } from '@/features/shared/theme/tokens'
import { AutocompletePill } from '@/features/shared/components/Headers/Header/styles'
import { Column, Row } from '@shipt/design-system-layouts'
import { MobileAddressButton } from '@/features/shared/components/Headers/GlobalHeader/MobileAddressButton'
import { MobileAddressWrapper } from '@/features/shared/components/Headers/MobileAddressWrapper'
import { NextDeliveryWindowText } from '@/features/shared/components/Headers/NextDeliveryWindowText'
import { SkeletonLoader } from '@/features/shared/components/SkeletonLoader'
import { useQueryRetailerETA } from '@/features/shop/services/DeliveryWindows/queries'
import { NextLink } from '@/features/shared/elements/Link'
import { useQueryCatNavCMS } from '@/features/cms/services/CMS/queries'
import { getCategoryUrl } from '@/features/cms/services/CMS/utils'
import { useUser, useUserStoreName } from '@/features/account/services/User/hooks'
import { getRetailerDealsUrl, routes } from '@shared/constants/routes'
import { useIsPage } from '@/features/shared/hooks/useIsPage'

const { DEALS, BUY_AGAIN, LISTS } = CATEGORY_HEADER_TYPE

export const MobileStoreHeader = ({
  shouldAnimate = false,
  logo,
  color,
}: {
  shouldAnimate?: boolean
  logo?: string
  color?: string
}) => {
  const pastThreshold = useScrollWithThreshold(90, { enabled: shouldAnimate })
  const isPastScrollThreshold = shouldAnimate ? pastThreshold : true

  const { data: retailerETAs } = useQueryRetailerETA()
  const { data: { category: navCategories = [] } = {}, isPending: isNavCategoriesPending } =
    useQueryCatNavCMS()
  const user = useUser()
  const storeName = useUserStoreName()
  const promiseType = retailerETAs?.delivery_etas[0]?.time_slot.promise_type

  // On the Order Details page we keep the animated logo + "Deliver to" address +
  // search header, but hide the delivery-window text and category navigation.
  const { ORDER_DETAIL: hideDeliveryWindowAndNav } = useIsPage()

  const isExpressOrPriority = promiseType === 'express' || promiseType === 'priority'
  const showExpressLayout = isExpressOrPriority && !hideDeliveryWindowAndNav
  const utilityLinkNames = new Set<string>([BUY_AGAIN, LISTS, DEALS])
  const utilityLinks = [
    ...(user?.id
      ? [
          { name: BUY_AGAIN, href: routes.BUY_AGAIN.url },
          { name: LISTS, href: routes.LISTS_HUB.url },
        ]
      : []),
    { name: DEALS, href: getRetailerDealsUrl(storeName) },
  ]

  return (
    <>
      <MobileHeaderContainer
        isPastScrollThreshold={isPastScrollThreshold}
        shouldAnimate={shouldAnimate}
        borderColor={color}
      >
        <HeaderWrapper contentMaxWidth>
          <Row justify="space-between" align="center" spacing="xl">
            <Row spacing="xl" align="center">
              <HamburgerMenuButton />
            </Row>
            <LogoContainer isPastScrollThreshold={isPastScrollThreshold}>
              <LogoAndStoreNav logo={logo} color={color} />
            </LogoContainer>
            <RetailerMobileAddressWrapper
              className="hideAboveTablet"
              justify="center"
              isPastScrollThreshold={isPastScrollThreshold}
              isExpressOrPriority={showExpressLayout}
            >
              <Column spacing="lg" align="center">
                <MobileAddressButton />
                {!!showExpressLayout && <NextDeliveryWindowText size="md" variant="primary" />}
              </Column>
            </RetailerMobileAddressWrapper>
            <AutocompleteWrapper
              data-is-past-scroll-threshold={isPastScrollThreshold}
              expressOrPriorityTopValue={showExpressLayout ? '3.5rem' : '0.5rem'}
            >
              <HeaderSearch />
            </AutocompleteWrapper>
            <RightMenuItems isPastScrollThreshold={isPastScrollThreshold} />
          </Row>
        </HeaderWrapper>
      </MobileHeaderContainer>
      {!!shouldAnimate && (
        <RetailerMobileContentBackgroundPositioner
          id={MOBILE_BG_POSITIONER_ID}
          isExpressOrPriority={showExpressLayout}
          hideCategoryNav={hideDeliveryWindowAndNav}
        >
          <RetailerMobileContentBackground
            isPastScrollThreshold={isPastScrollThreshold}
            borderColor={color}
            expressOrPriorityHeightValue={showExpressLayout ? 8 : 5}
          />
          {!hideDeliveryWindowAndNav && (
            <MobileCategoryNavRow
              id={MOBILE_STICKY_NAV_ID}
              as="nav"
              aria-label="Category navigation"
              aria-busy={isNavCategoriesPending}
              isPastScrollThreshold={isPastScrollThreshold}
            >
              {utilityLinks.map(({ name, href }) => (
                <CategoryNavLink key={name} href={href} prefetch={false}>
                  {name}
                </CategoryNavLink>
              ))}
              {!!isNavCategoriesPending && navCategories.length === 0 && (
                <>
                  <LoadingNavPill isLoading width="5rem" height="0.875rem" />
                  <LoadingNavPill isLoading width="5rem" height="0.875rem" />
                  <LoadingNavPill isLoading width="5rem" height="0.875rem" />
                </>
              )}
              {navCategories.map((category) => {
                const { name, id, url_slug } = category
                if (!name || !url_slug) return null
                if (utilityLinkNames.has(name)) return null

                return (
                  <CategoryNavLink
                    key={id ?? name}
                    href={getCategoryUrl(category, storeName)}
                    prefetch={false}
                  >
                    {name}
                  </CategoryNavLink>
                )
              })}
            </MobileCategoryNavRow>
          )}
        </RetailerMobileContentBackgroundPositioner>
      )}
    </>
  )
}

type IsPastScrollThreshold = { isPastScrollThreshold: boolean }

const MobileHeaderContainer = styled.div<{
  isPastScrollThreshold: boolean
  shouldAnimate: boolean
  borderColor?: string
}>`
  background-color: ${({ theme }) => theme.color.background.base.neutral.subtle_00};
  border-bottom: ${({ theme, borderColor, shouldAnimate, isPastScrollThreshold }) => {
    if (!isPastScrollThreshold || shouldAnimate) return 'none'
    return borderColor !== theme.color.border.base.neutral.inverse
      ? `2px solid ${borderColor}`
      : `0.5px solid ${theme.color.border.base.neutral.subtle}`
  }};
  pointer-events: auto;
`

const HeaderWrapper = styled(GridLayoutContainer)`
  position: relative;
  height: ${HEADER_DEFAULT_Y}px;
`

const AutocompleteWrapper = styled(AutocompletePill)<{
  'data-is-past-scroll-threshold': boolean
  expressOrPriorityTopValue: string
}>`
  @media ${screenSizes.max_mobile} {
    position: absolute;
    z-index: 10;
    width: auto;
    transition: all 0.6s ease;
    top: calc(100% + ${({ expressOrPriorityTopValue }) => expressOrPriorityTopValue});
    left: 1rem;
    right: 1rem;
    padding: ${spacingPx('xs')};
    margin-top: 1rem;
    height: 2.75rem;

    &[data-is-past-scroll-threshold='true'] {
      top: 50%;
      translate: 0 -50%;
      left: 3.4rem;
      right: 3.8rem;
      margin-top: 0;
    }
  }

  min-width: 12.5rem;
`

const LogoContainer = styled.div<IsPastScrollThreshold>`
  opacity: ${({ isPastScrollThreshold }) => (isPastScrollThreshold ? 0 : 1)};
  transition: opacity 0.6s ${({ isPastScrollThreshold }) => (isPastScrollThreshold ? '0.3s' : '')}
    ease;
`

const RetailerMobileContentBackgroundPositioner = styled.div<{
  isExpressOrPriority: boolean
  hideCategoryNav?: boolean
}>`
  /* do not remove, a fixed height is needed here currently to prevent a browser bug where
    the header bounces due to a conflict between position: sticky & our scroll threshold logic */
  height: ${({ isExpressOrPriority, hideCategoryNav }) => {
    // When the category nav is hidden (e.g. Order Details), still reserve enough
    // space for the animated background (5rem in the non-express layout).
    if (hideCategoryNav) return '5rem'
    return isExpressOrPriority ? '11rem' : '8rem'
  }};

  /* do not remove, this is to allow the elements covered by this element to be clickable */
  pointer-events: none;
`

const RetailerMobileContentBackground = styled.div<
  IsPastScrollThreshold & {
    borderColor?: string
    expressOrPriorityHeightValue: number
  }
>`
  height: ${({ isPastScrollThreshold, expressOrPriorityHeightValue }) =>
    isPastScrollThreshold ? 0 : expressOrPriorityHeightValue}rem;
  transition: height 0.6s ease;
  background-color: ${({ theme }) => theme.color.background.base.neutral.subtle_00};
  border-bottom: ${({ borderColor, theme }) =>
    borderColor !== theme.color.border.base.neutral.inverse
      ? `2px solid ${borderColor}`
      : `0.5px solid ${theme.color.border.base.neutral.subtle}`};
`

const MobileCategoryNavRow = styled(Row)<IsPastScrollThreshold>`
  overflow: auto hidden;
  scrollbar-width: none;
  white-space: nowrap;
  padding: 1rem 1rem 0;
  gap: 1rem;
  background-color: ${({ theme }) => theme.color.background.base.neutral.subtle_00};
  border-bottom: 0.5px solid ${({ theme }) => theme.color.border.base.neutral.subtle};
  transition: opacity 0.3s ease;
  height: 3rem;
  pointer-events: auto;
  position: relative;
  z-index: 11;
  opacity: 1;

  &::-webkit-scrollbar {
    display: none;
  }
`

const CategoryNavLink = styled(NextLink)`
  flex-shrink: 0;
  text-decoration: none;
  color: ${({ theme }) => theme.color.content.base.neutral.primary};
  font-size: 0.875rem;
`

const LoadingNavPill = styled(SkeletonLoader)`
  flex-shrink: 0;
  border-radius: 9999px;
  overflow: hidden;
`

const RetailerMobileAddressWrapper = styled(MobileAddressWrapper)<{
  isExpressOrPriority: boolean
  isPastScrollThreshold: boolean
}>`
  && {
    bottom: ${({ isExpressOrPriority }) => (isExpressOrPriority ? '-3.5rem' : '-1rem')};
    pointer-events: ${({ isPastScrollThreshold }) => (isPastScrollThreshold ? 'none' : 'auto')};
  }
`
